/**
 * PLANIFICACIÓN SEMANAL — store Zustand.
 * Estado de: planificaciones de la semana, inbox de tareas por planificar,
 * capacidad, conflictos y flujo de propuesta de Nexus (SOLO propuesta: el
 * guardado ocurre únicamente con la confirmación explícita de la persona).
 */
import { create } from 'zustand';
import {
  planningApi,
  type CapacitySummary,
  type InboxFilters,
  type InboxTask,
  type Planning,
  type PlanningConflict,
  type ProposedPlanning,
  type WeekProposal,
  type WorkSchedule,
} from './planningApi';
import {
  addDays,
  movedRange,
  snapDuration,
  weekStart as mondayOf,
} from '../components/planning/planningLogic';

export type NexusStage = 'IDLE' | 'ANALYZING' | 'PROPOSING' | 'REVIEW' | 'MODIFYING' | 'CONFIRMING' | 'DONE';

export interface PlanningState {
  /** Lunes de la semana visible (AAAA-MM-DD). */
  weekStart: string;
  items: Planning[];
  workSchedule: WorkSchedule | null;
  inbox: InboxTask[];
  inboxFilters: InboxFilters;
  capacity: CapacitySummary | null;
  conflicts: PlanningConflict[];
  loading: boolean;
  error: string | null;

  /** Flujo Nexus: ANALIZAR → PROPONER → REVISAR → MODIFICAR → CONFIRMAR. */
  nexus: {
    open: boolean;
    stage: NexusStage;
    proposals: ProposedPlanning[];
    notes: string[];
    confirmedCount: number;
    error: string | null;
    busy: boolean;
  };

  init: () => Promise<void>;
  setWeek: (monday: string) => void;
  prevWeek: () => void;
  nextWeek: () => void;
  goToToday: () => void;
  refresh: () => Promise<void>;

  loadInbox: (filters?: InboxFilters) => Promise<void>;
  setInboxFilters: (filters: InboxFilters) => void;

  /** Planifica una tarea del inbox (drag al calendario o diálogo «Mover a…»). */
  planTask: (taskId: string, date: string, time: string, durationMin: number) => Promise<void>;
  /** Mueve un planificado a otro día/hora. */
  movePlanning: (id: string, date: string, time: string) => Promise<void>;
  /** Cambia la duración (redimensión o diálogo). */
  resizePlanning: (id: string, durationMin: number) => Promise<void>;
  removePlanning: (id: string) => Promise<void>;

  openNexus: () => void;
  closeNexus: () => void;
  /** ANALIZAR + PROPONER. Nunca guarda. */
  nexusPropose: () => Promise<void>;
  /** MODIFICAR una propuesta antes de confirmar. */
  nexusEditProposal: (key: string, patch: Partial<ProposedPlanning['suggested']> & { durationMin?: number }) => void;
  /** CONFIRMAR: persiste las propuestas aceptadas vía POST /planning. */
  nexusConfirm: () => Promise<void>;
}

const todayISO = (): string => new Date().toISOString().slice(0, 10);

function errMsg(e: unknown, fallback: string): string {
  const m = e instanceof Error ? e.message : undefined;
  return m && !/^(Request failed with status code|Network Error|timeout of)/.test(m) ? m : fallback;
}

export const usePlanningStore = create<PlanningState>((set, get) => ({
  weekStart: mondayOf(todayISO()),
  items: [],
  workSchedule: null,
  inbox: [],
  inboxFilters: {},
  capacity: null,
  conflicts: [],
  loading: false,
  error: null,

  nexus: { open: false, stage: 'IDLE', proposals: [], notes: [], confirmedCount: 0, error: null, busy: false },

  init: async () => {
    await get().refresh();
    await get().loadInbox(get().inboxFilters);
  },

  setWeek: (monday) => {
    set({ weekStart: mondayOf(monday) });
    void get().refresh();
  },
  prevWeek: () => {
    set({ weekStart: addDays(get().weekStart, -7) });
    void get().refresh();
  },
  nextWeek: () => {
    set({ weekStart: addDays(get().weekStart, 7) });
    void get().refresh();
  },
  goToToday: () => {
    set({ weekStart: mondayOf(todayISO()) });
    void get().refresh();
  },

  refresh: async () => {
    const { weekStart } = get();
    const from = weekStart;
    const to = addDays(weekStart, 6);
    set({ loading: true, error: null });
    try {
      const [week, capacity, conflicts] = await Promise.all([
        planningApi.week(from, to),
        planningApi.capacity(from, to),
        planningApi.conflicts(from, to),
      ]);
      set({
        items: week.items,
        workSchedule: week.workSchedule,
        capacity,
        conflicts: conflicts.conflicts,
        loading: false,
      });
    } catch (e) {
      set({ loading: false, error: errMsg(e, 'No se pudo cargar la planificación de la semana') });
    }
  },

  loadInbox: async (filters) => {
    const f = filters ?? get().inboxFilters;
    try {
      const r = await planningApi.inbox(f);
      set({ inbox: r.tasks, inboxFilters: f });
    } catch (e) {
      set({ error: errMsg(e, 'No se pudieron cargar las tareas pendientes de planificar') });
    }
  },

  setInboxFilters: (filters) => {
    set({ inboxFilters: filters });
    void get().loadInbox(filters);
  },

  planTask: async (taskId, date, time, durationMin) => {
    const duration = snapDuration(durationMin);
    const { startsAt } = movedRange(date, time, duration);
    try {
      const created = await planningApi.create({ taskId, startsAt, durationMin: duration });
      set((s) => ({ items: [...s.items.filter((p) => p.id !== created.id), created], inbox: s.inbox.filter((t) => t.id !== taskId) }));
      await get().refresh();
    } catch (e) {
      set({ error: errMsg(e, 'No se pudo planificar la tarea') });
    }
  },

  movePlanning: async (id, date, time) => {
    const current = get().items.find((p) => p.id === id);
    if (!current) return;
    const { startsAt, durationMin } = movedRange(date, time, current.durationMin);
    try {
      const updated = await planningApi.update(id, { startsAt, durationMin });
      set((s) => ({ items: s.items.map((p) => (p.id === id ? updated : p)) }));
      await get().refresh();
    } catch (e) {
      set({ error: errMsg(e, 'No se pudo mover la planificación') });
    }
  },

  resizePlanning: async (id, durationMin) => {
    const duration = snapDuration(durationMin);
    const current = get().items.find((p) => p.id === id);
    if (!current) return;
    try {
      const updated = await planningApi.update(id, { startsAt: current.startsAt, durationMin: duration });
      set((s) => ({ items: s.items.map((p) => (p.id === id ? updated : p)) }));
      await get().refresh();
    } catch (e) {
      set({ error: errMsg(e, 'No se pudo cambiar la duración') });
    }
  },

  removePlanning: async (id) => {
    try {
      await planningApi.remove(id);
      set((s) => ({ items: s.items.filter((p) => p.id !== id) }));
      await get().refresh();
    } catch (e) {
      set({ error: errMsg(e, 'No se pudo quitar la planificación') });
    }
  },

  openNexus: () => set((s) => ({ nexus: { ...s.nexus, open: true, stage: s.nexus.stage === 'IDLE' ? 'ANALYZING' : s.nexus.stage, error: null } })),
  closeNexus: () => set((s) => ({ nexus: { ...s.nexus, open: false } })),

  nexusPropose: async () => {
    const { weekStart } = get();
    const from = weekStart;
    const to = addDays(weekStart, 6);
    set((s) => ({ nexus: { ...s.nexus, open: true, stage: 'ANALYZING', busy: true, error: null } }));
    let result: WeekProposal;
    try {
      result = await planningApi.proposeWeek(from, to);
      set((s) => ({ nexus: { ...s.nexus, stage: 'PROPOSING' } }));
    } catch (err: any) {
      // Backend Nexus no disponible → mostrar error real. NUNCA fabricar propuesta local.
      set((s) => ({ nexus: { ...s.nexus, busy: false, stage: 'IDLE', error: err?.message || 'Nexus backend no disponible — no se puede generar propuesta' } }));
      return;
    }
    set((s) => ({
      nexus: {
        ...s.nexus,
        busy: false,
        stage: 'REVIEW',
        proposals: result.proposals,
        notes: [...s.nexus.notes, ...result.notes],
        confirmedCount: 0,
      },
    }));
  },

  nexusEditProposal: (key, patch) =>
    set((s) => ({
      nexus: {
        ...s.nexus,
        stage: 'MODIFYING',
        proposals: s.nexus.proposals.map((p) =>
          p.key === key
            ? {
                ...p,
                suggested: {
                  date: patch.date ?? p.suggested?.date ?? s.weekStart,
                  time: patch.time ?? p.suggested?.time ?? '09:00',
                  durationMin: snapDuration(patch.durationMin ?? p.suggested?.durationMin ?? p.estimatedMinutes ?? 60),
                },
              }
            : p,
        ),
      },
    })),

  nexusConfirm: async () => {
    const proposals = get().nexus.proposals.filter((p) => p.suggested !== null);
    set((s) => ({ nexus: { ...s.nexus, stage: 'CONFIRMING', busy: true, error: null } }));
    let confirmed = 0;
    for (const p of proposals) {
      const s = p.suggested!;
      try {
        const { startsAt } = movedRange(s.date, s.time, s.durationMin);
        const created = await planningApi.create({ taskId: p.taskId, startsAt, durationMin: s.durationMin });
        set((st) => ({
          items: [...st.items.filter((x) => x.id !== created.id), created],
          inbox: st.inbox.filter((t) => t.id !== p.taskId),
          nexus: { ...st.nexus, confirmedCount: confirmed + 1 },
        }));
        confirmed += 1;
      } catch (e) {
        set((st) => ({ nexus: { ...st.nexus, error: errMsg(e, `No se pudo confirmar ${p.taskCode}`) } }));
        break;
      }
    }
    set((s) => ({ nexus: { ...s.nexus, busy: false, stage: 'DONE', confirmedCount: confirmed } }));
    await get().refresh();
  },
}));
